import { useState } from 'react';
import { Button, Group, Input, Stack, Text, TextInput } from '@mantine/core';
import { MAX_DOMAINS } from '@quizee/core';
import { readDomain } from '../brand/inheritance';

/**
 * The websites a brand's embeds are allowed to run on.
 *
 * This is the list that stops somebody pasting a publisher's embed snippet onto their own site and
 * posting responses into their poll, so it is worth explaining rather than validating in silence.
 * Every entry says what it will be saved as, every refusal says why in words, and a refusal from
 * the server is attached to the box that caused it rather than floated above the form.
 */
export function DomainsField({
  domains,
  onChange,
  canEdit,
  serverProblem,
  revealProblems = false,
}: {
  domains: readonly string[];
  onChange: (next: string[]) => void;
  canEdit: boolean;
  /** A refusal from the API, already matched to one entry. */
  serverProblem: { readonly index: number; readonly message: string } | null;
  /** After a failed save, every problem is shown whether or not the box has been left. */
  revealProblems?: boolean;
}) {
  const [touched, setTouched] = useState<readonly number[]>([]);

  function set(index: number, value: string) {
    onChange(domains.map((entry, at) => (at === index ? value : entry)));
  }

  return (
    <Input.Wrapper
      // A div rather than a label element: it names a group of boxes rather than one control, and
      // a label pointing at nothing is worse than no label for anybody using a screen reader.
      labelElement="div"
      label="Where this brand publishes"
      description="One entry covers everything under it: herald.co.uk also covers www.herald.co.uk and sport.herald.co.uk, so most brands need one line. Add another for a staging site."
    >
      <Stack gap="xs" mt="xs" role="group" aria-label="Where this brand publishes">
        {domains.map((entry, index) => {
          const verdict = readDomain(entry);
          const show = revealProblems || touched.includes(index);
          const server = serverProblem?.index === index ? serverProblem.message : null;
          const local = !verdict.ok && show && entry.trim() !== '' ? verdict.reason : null;

          return (
            <div key={index}>
              <Group gap="xs" wrap="nowrap" align="flex-start">
                <TextInput
                  aria-label={`Domain ${index + 1}`}
                  placeholder="herald.co.uk"
                  disabled={!canEdit}
                  value={entry}
                  error={server ?? local}
                  maxLength={300}
                  onBlur={() => setTouched((current) => [...current, index])}
                  onChange={(event) => set(index, event.currentTarget.value)}
                  style={{ flex: 1 }}
                />
                {canEdit && domains.length > 1 && (
                  <Button
                    variant="subtle"
                    color="gray"
                    size="compact-sm"
                    mt={6}
                    aria-label={`Remove domain ${index + 1}`}
                    onClick={() => onChange(domains.filter((_entry, at) => at !== index))}
                  >
                    Remove
                  </Button>
                )}
              </Group>
              {verdict.ok && verdict.tidied && (
                <Text size="xs" c="dimmed" mt={4}>
                  Saved as {verdict.domain}.
                </Text>
              )}
            </div>
          );
        })}

        {canEdit && domains.length < MAX_DOMAINS && (
          <Button
            variant="default"
            size="compact-sm"
            style={{ alignSelf: 'flex-start' }}
            onClick={() => onChange([...domains, ''])}
          >
            Add another domain
          </Button>
        )}
      </Stack>
    </Input.Wrapper>
  );
}
